import React, { useState } from 'react';
import { Award, Edit3, Plus, Calendar, User, MessageSquare } from 'lucide-react';
import TableauHonneurModal from './TableauHonneurModal';

const DecisionConseil = ({ bulletinId, tableauHonneur, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getDecisionColor = (decision) => {
    const colors = {
      'FELICITATION': 'text-green-700 bg-green-100 border-green-200',
      'ENCOURAGEMENT': 'text-blue-700 bg-blue-100 border-blue-200',
      'TABLEAU_D_HONNEUR': 'text-purple-700 bg-purple-100 border-purple-200',
      'AVERTISSEMENT': 'text-yellow-700 bg-yellow-100 border-yellow-200',
      'BLAME': 'text-red-700 bg-red-100 border-red-200',
      'AUCUN': 'text-gray-700 bg-gray-100 border-gray-200'
    };
    return colors[decision] || colors['AUCUN'];
  };

  const getDecisionLabel = (decision) => {
    const labels = {
      'FELICITATION': 'Félicitation',
      'ENCOURAGEMENT': 'Encouragement',
      'TABLEAU_D_HONNEUR': 'Tableau d\'honneur',
      'AVERTISSEMENT': 'Avertissement',
      'BLAME': 'Blâme',
      'AUCUN': 'Aucune décision'
    };
    return labels[decision] || 'Aucune décision';
  };

  return (
    <>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 shadow-lg border border-amber-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Award className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Décision du conseil des professeurs</h3>
              <p className="text-amber-600 font-medium">Gestion des honneurs et sanctions</p>
            </div>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className={`inline-flex items-center px-6 py-3 text-sm font-semibold rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 ${
              tableauHonneur
                ? 'text-blue-600 bg-blue-50 border-2 border-blue-200 hover:bg-blue-100 hover:border-blue-300 shadow-md hover:shadow-lg'
                : 'text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg hover:shadow-xl'
            }`}
          >
            {tableauHonneur ? (
              <>
                <Edit3 className="h-4 w-4 mr-2" />
                Modifier la décision
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Créer une décision
              </>
            )}
          </button>
        </div>

        {tableauHonneur ? (
          <div className="space-y-6">
            {/* Décision principale */}
            <div className="bg-white rounded-xl p-6 border border-amber-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-semibold text-lg">Décision retenue :</span>
                <span className={`px-4 py-2 rounded-full text-sm font-bold border-2 ${getDecisionColor(tableauHonneur.decision_conseil)}`}>
                  {getDecisionLabel(tableauHonneur.decision_conseil)}
                </span>
              </div>
            </div>

            {/* Appréciation */}
            {tableauHonneur.appreciation_conseil && (
              <div className="bg-white rounded-xl p-6 border border-amber-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <MessageSquare className="h-5 w-5 text-amber-600" />
                  <span className="text-gray-700 font-semibold text-lg">Appréciation du conseil :</span>
                </div>
                <div className="bg-gradient-to-r from-gray-50 to-amber-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-800 text-base leading-relaxed italic">
                    "{tableauHonneur.appreciation_conseil}"
                  </p>
                </div>
              </div>
            )}

            {/* Informations de saisie */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border border-amber-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-3">
                  <Calendar className="h-5 w-5 text-amber-600" />
                  <span className="text-gray-700 font-semibold">Date de saisie :</span>
                </div>
                <p className="text-gray-800 text-lg font-medium">
                  {new Date(tableauHonneur.date_decision).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              
              {tableauHonneur.decide_par_nom && (
                <div className="bg-white rounded-xl p-6 border border-amber-200 shadow-sm">
                  <div className="flex items-center space-x-2 mb-3">
                    <User className="h-5 w-5 text-amber-600" />
                    <span className="text-gray-700 font-semibold">Saisi par :</span>
                  </div>
                  <p className="text-gray-800 text-lg font-medium">
                    {tableauHonneur.decide_par_nom}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-amber-200">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="h-10 w-10 text-amber-500" />
            </div>
            <h4 className="text-xl font-semibold text-gray-700 mb-3">
              Aucune décision enregistrée
            </h4>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Aucune décision du conseil des professeurs n'a été prise pour ce bulletin.
            </p>
            <p className="text-sm text-amber-600 font-medium">
              Cliquez sur "Créer une décision" pour commencer.
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      <TableauHonneurModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        bulletinId={bulletinId}
        onUpdate={onUpdate}
      />
    </>
  );
};

export default DecisionConseil;
